/**
 * Core Web Vitals Performance Monitoring Service
 *
 * Tracks and reports Core Web Vitals metrics:
 * - LCP (Largest Contentful Paint)
 * - FID (First Input Delay) / INP (Interaction to Next Paint)
 * - CLS (Cumulative Layout Shift)
 * - TTFB (Time to First Byte)
 * - FCP (First Contentful Paint)
 */

import type { Metric } from 'web-vitals';

// Thresholds based on Google's Core Web Vitals guidelines
export const VITALS_THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 }, // milliseconds
  FID: { good: 100, needsImprovement: 300 },   // milliseconds
  INP: { good: 200, needsImprovement: 500 },   // milliseconds
  CLS: { good: 0.1, needsImprovement: 0.25 },  // score
  TTFB: { good: 800, needsImprovement: 1800 }, // milliseconds
  FCP: { good: 1800, needsImprovement: 3000 }, // milliseconds
} as const;

export type VitalName = keyof typeof VITALS_THRESHOLDS;

export type VitalRating = 'good' | 'needs-improvement' | 'poor';

export interface VitalMetric {
  name: VitalName;
  value: number;
  rating: VitalRating;
  delta: number;
  id: string;
  navigationType?: string;
  timestamp: number;
  url: string;
  userAgent?: string;
}

export interface VitalsReport {
  sessionId: string;
  metrics: VitalMetric[];
  timestamp: number;
  url: string;
  connectionType?: string;
  deviceMemory?: number;
  hardwareConcurrency?: number;
}

/**
 * Get the rating for a vital metric based on thresholds
 */
export function getVitalRating(name: VitalName, value: number): VitalRating {
  const thresholds = VITALS_THRESHOLDS[name];
  if (value <= thresholds.good) return 'good';
  if (value <= thresholds.needsImprovement) return 'needs-improvement';
  return 'poor';
}

/**
 * Get the color class for a vital rating
 */
export function getRatingColor(rating: VitalRating): string {
  switch (rating) {
    case 'good':
      return 'text-green-600 bg-green-100';
    case 'needs-improvement':
      return 'text-yellow-600 bg-yellow-100';
    case 'poor':
      return 'text-red-600 bg-red-100';
  }
}

/**
 * Generate a unique session ID for tracking
 */
function generateSessionId(): string {
  return `${Date.now()}-${crypto.randomUUID()}`;
}

/**
 * Get session ID from storage or create new one
 */
function getSessionId(): string {
  if (typeof window === 'undefined') return 'server';

  let sessionId = sessionStorage.getItem('bf_perf_session');
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem('bf_perf_session', sessionId);
  }
  return sessionId;
}

/**
 * Get connection info if available
 */
function getConnectionInfo(): { connectionType?: string } {
  if (typeof navigator === 'undefined') return {};

  const connection = (navigator as Navigator & {
    connection?: { effectiveType?: string };
  }).connection;

  return {
    connectionType: connection?.effectiveType,
  };
}

/**
 * Get device info if available
 */
function getDeviceInfo(): { deviceMemory?: number; hardwareConcurrency?: number } {
  if (typeof navigator === 'undefined') return {};

  return {
    deviceMemory: (navigator as Navigator & { deviceMemory?: number }).deviceMemory,
    hardwareConcurrency: navigator.hardwareConcurrency,
  };
}

/**
 * Transform web-vitals Metric to VitalMetric
 */
function transformMetric(metric: Metric): VitalMetric {
  const name = metric.name as VitalName;
  return {
    name,
    value: metric.value,
    rating: getVitalRating(name, metric.value),
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType,
    timestamp: Date.now(),
    url: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  };
}

/**
 * Report metrics to the analytics endpoint
 */
async function reportToEndpoint(metrics: VitalMetric[]): Promise<void> {
  try {
    const report: VitalsReport = {
      sessionId: getSessionId(),
      metrics,
      timestamp: Date.now(),
      url: typeof window !== 'undefined' ? window.location.href : '',
      ...getConnectionInfo(),
      ...getDeviceInfo(),
    };

    // Send to our API endpoint
    const response = await fetch('/api/vitals', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report),
      // Use keepalive to ensure the request completes even if page unloads
      keepalive: true,
    });

    if (!response.ok) {
      console.warn('[Performance] Failed to report vitals:', response.statusText);
    }
  } catch (error) {
    // Silently fail - don't impact user experience
    console.warn('[Performance] Error reporting vitals:', error);
  }
}

/**
 * Report to PostHog analytics if available
 */
function reportToPosthog(metric: VitalMetric): void {
  if (typeof window === 'undefined') return;

  const posthog = (window as Window & { posthog?: { capture: (event: string, props?: Record<string, unknown>) => void } }).posthog;

  if (posthog) {
    posthog.capture('web_vital', {
      vital_name: metric.name,
      vital_value: metric.value,
      vital_rating: metric.rating,
      vital_delta: metric.delta,
      vital_id: metric.id,
      navigation_type: metric.navigationType,
      url: metric.url,
    });
  }
}

// Buffer for batching metrics
let metricsBuffer: VitalMetric[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Flush buffered metrics to endpoints
 */
async function flushMetrics(): Promise<void> {
  if (metricsBuffer.length === 0) return;

  const metricsToSend = [...metricsBuffer];
  metricsBuffer = [];

  await reportToEndpoint(metricsToSend);
}

/**
 * Schedule a flush of the metrics buffer
 */
function scheduleFlush(): void {
  if (flushTimeout) return;

  flushTimeout = setTimeout(() => {
    flushTimeout = null;
    flushMetrics();
  }, 5000); // Batch and send every 5 seconds
}

/**
 * Handle a web vital metric
 */
export function handleVital(metric: Metric): void {
  const vitalMetric = transformMetric(metric);

  // Report immediately to PostHog
  reportToPosthog(vitalMetric);

  // Buffer for batch reporting to our endpoint
  metricsBuffer.push(vitalMetric);
  scheduleFlush();

  // Log in development
  if (process.env.NODE_ENV === 'development') {
    console.log(
      `[Performance] ${metric.name}:`,
      metric.value.toFixed(metric.name === 'CLS' ? 3 : 0),
      `(${vitalMetric.rating})`
    );
  }
}

/**
 * Initialize web vitals monitoring
 * Should be called once on the client side
 */
export async function initWebVitals(): Promise<void> {
  if (typeof window === 'undefined') return;

  try {
    const { onCLS, onFCP, onINP, onLCP, onTTFB } = await import('web-vitals');

    // Register handlers for all Core Web Vitals
    // Note: FID (First Input Delay) was deprecated in web-vitals v4, replaced by INP
    onCLS(handleVital);
    onFCP(handleVital);
    onINP(handleVital);
    onLCP(handleVital);
    onTTFB(handleVital);

    // Flush metrics when page is hidden (user navigates away)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushMetrics();
      }
    });

    // Also flush on page unload
    window.addEventListener('pagehide', () => {
      flushMetrics();
    });
  } catch (error) {
    console.warn('[Performance] Failed to initialize web vitals:', error);
  }
}

/**
 * Format a vital value for display
 */
export function formatVitalValue(name: VitalName, value: number): string {
  if (name === 'CLS') {
    return value.toFixed(3);
  }
  return `${Math.round(value)} ms`;
}

/**
 * Get a human-readable description of a vital
 */
export function getVitalDescription(name: VitalName): string {
  switch (name) {
    case 'LCP':
      return 'Largest Contentful Paint - measures loading performance';
    case 'FID':
      return 'First Input Delay - measures interactivity';
    case 'INP':
      return 'Interaction to Next Paint - measures overall responsiveness';
    case 'CLS':
      return 'Cumulative Layout Shift - measures visual stability';
    case 'TTFB':
      return 'Time to First Byte - measures server response time';
    case 'FCP':
      return 'First Contentful Paint - measures initial render time';
  }
}

/**
 * Calculate overall performance score (0-100)
 */
export function calculatePerformanceScore(metrics: VitalMetric[]): number {
  if (metrics.length === 0) return 0;

  const weights = {
    LCP: 0.25,
    INP: 0.25,
    CLS: 0.25,
    FCP: 0.15,
    TTFB: 0.10,
    FID: 0, // FID is being replaced by INP
  };

  let totalWeight = 0;
  let weightedScore = 0;

  for (const metric of metrics) {
    const weight = weights[metric.name] || 0;
    if (weight === 0) continue;

    let score: number;
    switch (metric.rating) {
      case 'good':
        score = 100;
        break;
      case 'needs-improvement':
        score = 50;
        break;
      case 'poor':
        score = 0;
        break;
    }

    weightedScore += score * weight;
    totalWeight += weight;
  }

  return totalWeight > 0 ? Math.round(weightedScore / totalWeight * 100) / 100 : 0;
}
